import { css } from '@emotion/react';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TextInputComponent } from '~/_shared/baseComponents/inputs';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import { useTranslation } from '~/_shared/utils/hooks';

const territoryGroupNameStyle = ({ theme, isPlaceholder }: ThemeProps<{ isPlaceholder: boolean }>) => css({
  height: 40,
  backgroundColor: theme.backgroundColors.secondary,
  color: isPlaceholder ? theme.textColors.disabled : undefined,
});

const generateButtonContainerStyle = css({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 20,
});

const generateButtonStyle = css({
  width: '100%',
  paddingTop: 13,
  paddingBottom: 13,
});

const alertStyles = css({
  marginTop: 16,
});

const checkboxesContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 16,
});

export type NameStepComponentProps = Readonly<{
  groupName: string;
  onChange: (groupName: string) => void;
  onContinue: () => void;
  optimizedTerritoriesRequestError: string | null;
  onCloseOptimizedTerritoriesRequestError: () => void;
  isSubmitDisabled: boolean;
  options?: Array<{ id: string; label: string; value: boolean }>;
  onOptionChange?: (id: string, value: boolean) => void;
}>;

export const NameStepComponent: FC<NameStepComponentProps> = ({
  groupName,
  onChange,
  onContinue,
  optimizedTerritoriesRequestError,
  onCloseOptimizedTerritoriesRequestError,
  isSubmitDisabled,
  options,
  onOptionChange,
}: NameStepComponentProps) => {
  const [t] = useTranslation();
  const theme = useTheme();

  const onSubmit = useCallback(() => {
    onChange(groupName.trim());
    onContinue();
  }, [groupName, onContinue, onChange]);

  const handleCheckboxChange = useCallback((optionId: string) => {
    if (onOptionChange) {
      const currentValue = options?.find((opt) => opt.id === optionId)?.value ?? false;
      onOptionChange(optionId, !currentValue);
    }
  }, [options, onOptionChange]);

  return (
    <>
      <TextInputComponent
        css={territoryGroupNameStyle({ theme, isPlaceholder: groupName.length === 0 })}
        value={groupName}
        onChange={onChange}
        type="text"
        placeholder={t('boundaryAI.Territory Group Name')}
        icon={faGlobe}
      />
      {!!optimizedTerritoriesRequestError && (
        <AlertComponent
          css={alertStyles}
          type="danger"
          onClose={onCloseOptimizedTerritoriesRequestError}
        >
          {optimizedTerritoriesRequestError}
        </AlertComponent>
      )}
      {options && notEmpty(options) && (
        <div css={checkboxesContainerStyle}>
          {options.map((option) => (
            <CheckboxComponent
              key={option.id}
              isChecked={option.value}
              text={option.label}
              checkedSetter={() => handleCheckboxChange(option.id)}
            />
          ))}
        </div>
      )}
      <div css={generateButtonContainerStyle}>
        <ButtonComponent
          css={generateButtonStyle}
          text={t('boundaryAI.Generate Territory')}
          onClick={onSubmit}
          isDisabled={isSubmitDisabled}
        />
      </div>
    </>
  );
};
